import { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { LogOut, Menu, Moon, Settings, Sun, X } from 'lucide-react';
import logoImg from 'images/logo.svg';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitcher } from 'hooks/useLanguageSwitcher';
import { LanguageToggle } from 'components/Header/LanguageToggle/LanguageToggle';

import { selectIsLoggedIn } from 'redux/selectors';
import { DisplayBalance } from 'components/Header/Balance';
import { ModaLogout } from 'components/Modal/ModalLogout/ModalLogout';
import { useLogout } from 'hooks/logoutHook';

export const Header = ({ isDark = false, toggleTheme }) => {
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const { t } = useTranslation();
  const { activeCode, setLanguageByCode } = useLanguageSwitcher();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const mobileNavRef = useRef(null);

  const closeMobileNav = () => setMobileNavOpen(false);

  const { modalOpen, toggleModal, handleClick } = useLogout(closeMobileNav);

  useEffect(() => {
    const onDoc = e => {
      if (
        mobileNavOpen &&
        mobileNavRef.current &&
        !mobileNavRef.current.contains(e.target)
      ) {
        closeMobileNav();
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [mobileNavOpen]);

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') closeMobileNav();
    };
    if (mobileNavOpen) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileNavOpen]);

  const menuPanelBase =
    'absolute right-0 top-full z-[310] mt-2 w-52 overflow-hidden rounded-2xl border py-2 text-left shadow-lg ';
  const menuItemBase =
    'flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium transition ';
  const menuMuted = isDark
    ? 'text-slate-200 hover:bg-slate-800/90'
    : 'text-slate-800 hover:bg-orange-50/80';
  const dangerItem = isDark
    ? 'text-orange-400 hover:bg-slate-800/90'
    : 'text-[#FF7601] hover:bg-orange-50/80';

  const iconCircleBtn = `inline-flex h-10 w-10 items-center justify-center rounded-full border transition ${
    isDark
      ? 'border-slate-700 bg-slate-800 text-orange-400 hover:bg-slate-700'
      : 'border-orange-100 bg-white text-[#FF7601] hover:bg-orange-50'
  }`;

  return (
    <header
      className={`fixed left-0 top-0 z-[300] w-full border-b px-4 py-3 shadow-sm backdrop-blur md:px-8 ${
        isLoggedIn ? 'md:left-64 md:w-[calc(100%-16rem)]' : ''
      } ${
        isDark
          ? 'border-slate-800 bg-slate-900/95'
          : 'border-slate-200 bg-white/95'
      }`}
    >
      <div className="flex w-full min-h-10 items-center justify-between gap-3">
        <Link
          to={isLoggedIn ? '/home' : '/'}
          className="flex shrink-0 items-center outline-none focus-visible:ring-2 focus-visible:ring-[#FF7601] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded-md"
          aria-label="FinTrack"
        >
          <img
            src={logoImg}
            alt=""
            width={40}
            height={40}
            className="h-9 w-auto object-contain md:h-10"
            decoding="async"
          />
        </Link>
        <div className="flex min-w-0 shrink-0 items-center gap-3">
          {isLoggedIn && (
            <>
              <div className="min-w-0">
                <DisplayBalance />
              </div>
              <div className="relative md:hidden" ref={mobileNavRef}>
                <button
                  type="button"
                  onClick={() => setMobileNavOpen(o => !o)}
                  aria-expanded={mobileNavOpen}
                  aria-haspopup="true"
                  aria-label={
                    mobileNavOpen
                      ? t('header.closeMobileMenu')
                      : t('header.openMobileMenu')
                  }
                  className={iconCircleBtn}
                >
                  {mobileNavOpen ? <X size={18} /> : <Menu size={18} />}
                </button>
                {mobileNavOpen && (
                  <div
                    className={`${menuPanelBase} ${
                      isDark
                        ? 'border-slate-700 bg-slate-900'
                        : 'border-slate-200 bg-white'
                    }`}
                    role="menu"
                  >
                    <NavLink
                      to="/personal-data"
                      role="menuitem"
                      className={`${menuItemBase} ${menuMuted}`}
                      onClick={closeMobileNav}
                    >
                      <Settings size={18} aria-hidden />
                      {t('pageName.settings')}
                    </NavLink>
                    <button
                      type="button"
                      role="menuitem"
                      className={`${menuItemBase} ${menuMuted}`}
                      onClick={() => {
                        toggleTheme();
                        closeMobileNav();
                      }}
                      aria-label="Toggle theme"
                    >
                      {isDark ? <Sun size={18} /> : <Moon size={18} />}
                      {t('header.theme')}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className={`${menuItemBase} ${dangerItem}`}
                      onClick={toggleModal}
                    >
                      <LogOut size={18} strokeWidth={2.25} aria-hidden />
                      {t('carousel.exit')}
                    </button>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={toggleTheme}
                aria-label="Toggle theme"
                className={`hidden md:inline-flex ${iconCircleBtn}`}
              >
                {isDark ? <Sun size={17} /> : <Moon size={17} />}
              </button>
              <button
                type="button"
                onClick={toggleModal}
                aria-label={t('carousel.exit')}
                className={`hidden h-10 w-10 shrink-0 items-center justify-center rounded-full border transition md:inline-flex ${
                  isDark
                    ? 'border-slate-700 bg-slate-800 text-orange-400 hover:bg-slate-700'
                    : 'border-orange-100 bg-white text-[#FF7601] hover:bg-orange-50'
                }`}
              >
                <LogOut size={17} strokeWidth={2.25} />
              </button>
            </>
          )}
          {!isLoggedIn && (
            <>
              <LanguageToggle
                isDark={isDark}
                activeCode={activeCode}
                onChange={setLanguageByCode}
                ariaLabel={t('header.language')}
              />
              <button
                type="button"
                onClick={toggleTheme}
                aria-label="Toggle theme"
                className={iconCircleBtn}
              >
                {isDark ? <Sun size={17} /> : <Moon size={17} />}
              </button>
            </>
          )}
          {modalOpen && (
            <ModaLogout onConfirm={handleClick} onClose={toggleModal} />
          )}
        </div>
      </div>
    </header>
  );
};
